import moment from 'moment';
import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../reducers';
import {
  API_ENV,
  API_BASE_URL,
  COMPANY_NAME,
  COMPANY_BASE_URL,
  BOOKING_BASE_URL,
  INSTAGRAM_BOOKING_BASE_URL,
  FSS_BASE_URL,
} from '../constants';
import {
  UnauthorizedError,
  UnconfirmedEmailError,
} from '../utils/custom-errors';
import {
  GRAVITY_CALENDAR_START_DATE,
  WSB_MERCHANT_SOURCE,
} from '../shared/constants/merchant';
import { BETA_TOGGLES, isBetaToggleEnabled } from '../features/beta/toggles';
import { resolveUpgradeEligibility } from '../features/bookings-upgrade/eligibility';

export const getMerchantId = (state: RootState) => state.currentMerchant.id;

export const getMerchantTimeZone = (state: RootState) =>
  state.currentMerchant.timeZone;

export const isWsbMerchant = (state: Partial<RootState>) =>
  state.currentMerchant?.source === WSB_MERCHANT_SOURCE;

export const isGravityCalendarMerchant = (state: Partial<RootState>) => {
  if (window.localStorage.getItem('calendar_gravity') === 'true') return true;

  if (state.currentMerchant?.whiteLabel === 'localsearch') return false;

  if (isWsbMerchant(state)) return false;

  const merchantCreatedAt = state.currentMerchant?.createdAt;

  return (
    !!merchantCreatedAt &&
    new Date(merchantCreatedAt) >= new Date(GRAVITY_CALENDAR_START_DATE)
  );
};

/**
 * Composes the appointment allowance handed to micro-apps as a flag.
 *
 * Built here rather than in each micro-app: app-shell owns the environment's
 * upgrade URL, and one source of the used count keeps the shell's dialog and
 * the widget's banner in agreement. Shares `resolveUpgradeEligibility` with
 * `BookingsUpgradeContext` so the two cannot drift.
 */
function toAppointmentLimitSettings(
  currentMerchant: RootState['currentMerchant']
) {
  return resolveUpgradeEligibility(
    currentMerchant,
    isBetaToggleEnabled(BETA_TOGGLES.bookingsUpgrade.key)
  );
}

export const getAppSettings = createSelector(
  (state: RootState) => state.currentAccount,
  (state: RootState) => state.currentMerchant,
  (state: RootState) => state.whiteLabel,
  (currentAccount, currentMerchant, whiteLabel) => ({
    organizationId: currentAccount.organizationId,
    whiteLabel: currentMerchant.whiteLabel,
    merchantAccountId: currentAccount.id,
    merchantId: currentMerchant.id,
    source: currentMerchant.source,
    locale: currentAccount.locale,
    whiteLabelConfiguration: whiteLabel.config,
    company: COMPANY_NAME,
    companyBaseUrl: COMPANY_BASE_URL,
    apiEnv: API_ENV,
    apiBaseUrl: API_BASE_URL,
    fssBaseUrl: FSS_BASE_URL,
    bookingBaseUrl: BOOKING_BASE_URL,
    instagramBookingBaseUrl: INSTAGRAM_BOOKING_BASE_URL,
    isSelfSignUp: currentMerchant.isSelfSignUp,
    trialEndsOn: currentMerchant.trialEndsOn,
    trialDaysLeft:
      currentMerchant.trialDaysLeft === null
        ? 0
        : currentMerchant.trialDaysLeft,
    featureToggles: currentMerchant.featureToggles,
    isFreeProduct: !!currentMerchant.featureToggles.free_product,
    subscriptionPackageCode: currentMerchant.subscriptionPackageCode,
    packageCode: currentMerchant.packageCode,
    subscriptionPartner: currentMerchant.subscriptionPartner ?? null,
    sessionRole: currentMerchant.sessionRole,
    paymentMethods: currentMerchant?.paymentSettings?.paymentMethods || [],
    // The beta toggle is read from localStorage once per shell load rather than
    // through a hook: this selector feeds the `settings` object handed to
    // micro-apps at create() time, and the Elm apps cannot re-read their flags.
    // Flipping the switch on /beta therefore needs a reload to reach them — see
    // features/beta/toggles.ts.
    appointmentLimit: toAppointmentLimitSettings(currentMerchant),
  })
);

export const isTrialPeriodExpired = ({ currentMerchant }: RootState) =>
  currentMerchant.trialDaysLeft === 0;

export const getFeatureToggles = (state: RootState) =>
  state.currentMerchant.featureToggles;

export const getUiVisibility = (state: RootState) =>
  state.currentMerchant.uiVisibility;

export const hasAppError = (state: RootState) => !!state.currentApp.error;

export const getAppErrorMessage = (state: RootState) =>
  hasAppError(state) ? state.currentApp.error!.message : '';

export const hasOverlayError = (state: RootState) =>
  !!state.currentOverlay.error;

export const getOverlayErrorMessage = (state: RootState) =>
  hasOverlayError(state) ? state.currentOverlay.error!.message : '';

export const hasInitializationError = (state: RootState) =>
  !!state.initialization.error;

export const getInitializationErrorName = (state: RootState) =>
  hasInitializationError(state) ? state.initialization.error!.name : '';

export const getInitializationErrorMessage = (state: RootState) =>
  hasInitializationError(state) ? state.initialization.error!.message : '';

export const hasUnauthorizedError = (state: RootState) =>
  hasInitializationError(state) &&
  state.initialization.error!.name === UnauthorizedError.name;

export const hasUnconfirmedEmailError = (state: RootState) =>
  hasInitializationError(state) &&
  state.initialization.error!.name === UnconfirmedEmailError.name;

export const overlayIsOpen = (state: RootState) => !!state.currentOverlay.name;

export const shouldBlur = (state: RootState) => {
  const {
    currentOverlay: { name = '' },
  } = state;
  return overlayIsOpen(state) && name !== 'launchpad';
};

export const getUnreadAppointmentCount = (state: RootState) =>
  state.notifications.appointmentCount;

export const getMessageCount = (state: RootState) =>
  state.notifications.messageCount;

export const getShowAppointments = (state: RootState) =>
  state.notifications.ui.open;

export const getNotificationAppointments = (state: RootState) =>
  state.notifications.appointments;

export const getFirstUnreadClientMessage = (state: RootState) =>
  state.notifications.clientMessages.find((m) => m.closed === false);

export const notificationsPopupIsOpen = (state: RootState) =>
  state.notifications.ui.open;

export const notificationIsLoaded = (state: RootState) =>
  state.notifications.ui.loaded;

interface SetupTodoItem {
  status: string;
}

// setupTodo isn't part of CurrentMerchantState and isn't populated by the currentMerchant reducer; returns zeros unless state is augmented elsewhere.
export const getTaskProgress = (state: RootState) => {
  const currentMerchant =
    state.currentMerchant as RootState['currentMerchant'] & {
      setupTodo?: { items?: SetupTodoItem[] };
    };
  if (!currentMerchant.setupTodo) return { completed: 0, total: 0 };
  const {
    setupTodo: { items = [] },
  } = currentMerchant;
  const completed = items.filter(({ status }) => status === 'COMPLETED').length;
  const total = items.length;
  return { completed, total };
};

export const hasUserJustLoggedIn = (state: RootState) => {
  const tenSeconds = 10000;
  const timeSinceLastLogin = moment().diff(
    state.currentAccount.currentSigninAt
  );
  return timeSinceLastLogin < tenSeconds;
};

export const getSessionPermissions = (state: RootState) =>
  state.sessionPermissions;
